/**
 * Shortcut cheat sheet (F1 / ?), command palette (⌘K) and board search
 * (⌘F, where the app has no native one). Self-contained styles so the board
 * apps don't need the shared stylesheet.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { BOARD_KEYMAP, GROUP_TITLES, describe, detectPlatform, type CommandId, type KeyBinding, type KeyGroup, type Platform } from '../keymap';

export type OverlayMode = 'shortcuts' | 'palette' | 'search';

export interface SearchHit {
  id: string;
  label: string;
  detail?: string;
}

export interface KeymapOverlayProps {
  mode: OverlayMode;
  onClose(): void;
  /** Palette: run a command (the same handler the key would run). */
  onRun?(id: CommandId): void;
  /** Palette: which commands can run here (others are hidden). */
  available?: (id: CommandId) => boolean;
  /** Search: board-specific text search. */
  onSearch?(query: string): SearchHit[];
  onPick?(hit: SearchHit): void;
  /** Cheat sheet: notes for keys the app maps differently from Miro. */
  notes?: Partial<Record<CommandId, string>>;
  keymap?: readonly KeyBinding[];
  platform?: Platform;
  /** Cheat sheet: a line under the groups (e.g. which board keys don't apply here). */
  footnote?: string;
  /** Search box title (default "Search the board"). */
  searchTitle?: string;
}

const CSS = `
.rlk-backdrop{position:fixed;inset:0;z-index:100000;background:rgba(15,18,30,.35);display:flex;align-items:flex-start;justify-content:center;padding-top:9vh;font:14px/1.4 system-ui,-apple-system,"Segoe UI",sans-serif}
.rlk-panel{background:var(--rlk-bg,#fff);color:var(--rlk-fg,#1d1d24);border-radius:12px;box-shadow:0 20px 60px rgba(0,0,0,.25);width:min(560px,calc(100vw - 32px));max-height:78vh;display:flex;flex-direction:column;overflow:hidden}
.rlk-panel.is-wide{width:min(880px,calc(100vw - 32px))}
.rlk-head{display:flex;align-items:center;gap:8px;padding:12px 14px;border-bottom:1px solid rgba(0,0,0,.08)}
.rlk-head h2{font-size:15px;margin:0;flex:1}
.rlk-input{flex:1;border:0;outline:0;font:inherit;font-size:15px;background:transparent;color:inherit}
.rlk-x{border:0;background:transparent;font-size:18px;cursor:pointer;color:inherit;opacity:.6}
.rlk-body{overflow:auto;padding:6px 0}
.rlk-cols{display:grid;grid-template-columns:repeat(auto-fit,minmax(250px,1fr));gap:4px 22px;padding:8px 16px 14px}
.rlk-group h3{font-size:11px;text-transform:uppercase;letter-spacing:.06em;opacity:.55;margin:10px 0 4px}
.rlk-row{display:flex;justify-content:space-between;gap:12px;padding:3px 0;font-size:13px}
.rlk-row small{display:block;opacity:.55;font-size:11px}
.rlk-key{font:12px ui-monospace,SFMono-Regular,Menlo,monospace;background:rgba(0,0,0,.06);border-radius:5px;padding:1px 6px;white-space:nowrap;align-self:flex-start}
.rlk-item{display:flex;justify-content:space-between;gap:12px;width:100%;border:0;background:transparent;color:inherit;text-align:left;font:inherit;padding:7px 16px;cursor:pointer}
.rlk-item.is-active{background:rgba(66,98,255,.12)}
.rlk-item small{opacity:.6}
.rlk-empty{padding:14px 16px;opacity:.6}
@media (prefers-color-scheme:dark){.rlk-panel{--rlk-bg:#23242b;--rlk-fg:#ececf1}.rlk-key{background:rgba(255,255,255,.1)}.rlk-head{border-color:rgba(255,255,255,.1)}}
`;

function useStyles() {
  useEffect(() => {
    if (document.getElementById('rlk-styles')) return;
    const s = document.createElement('style');
    s.id = 'rlk-styles';
    s.textContent = CSS;
    document.head.appendChild(s);
  }, []);
}

export function KeymapOverlay(p: KeymapOverlayProps) {
  useStyles();
  const platform = p.platform ?? detectPlatform();
  const keymap = p.keymap ?? BOARD_KEYMAP;
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    input.current?.focus();
  }, [p.mode]);

  const commands = useMemo(() => {
    if (p.mode !== 'palette') return [];
    const q = query.trim().toLowerCase();
    return keymap.filter((b) => b.combos.length && b.id !== 'app.palette' && (!p.available || p.available(b.id)) && (!q || b.label.toLowerCase().includes(q) || b.id.includes(q)));
  }, [p.mode, query, keymap, p.available]); // eslint-disable-line react-hooks/exhaustive-deps

  const hits = useMemo(() => (p.mode === 'search' && query.trim() && p.onSearch ? p.onSearch(query.trim()) : []), [p.mode, query, p.onSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  const list = p.mode === 'palette' ? commands.length : hits.length;
  useEffect(() => setActive(0), [query]);

  const choose = (i: number) => {
    if (p.mode === 'palette') {
      const c = commands[i];
      if (!c) return;
      p.onClose();
      p.onRun?.(c.id);
    } else if (p.mode === 'search') {
      const h = hits[i];
      if (h) p.onPick?.(h); // keep the box open so Enter steps through results
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation();
    if (e.key === 'Escape') return p.onClose();
    if (p.mode === 'shortcuts') {
      if (e.key === '?' || e.key === 'F1') p.onClose();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(list - 1, a + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(0, a - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (p.mode === 'search' && list) {
        const next = e.shiftKey ? (active - 1 + list) % list : active;
        choose(next);
        if (!e.shiftKey) setActive((active + 1) % list);
        else setActive(next);
      } else choose(active);
    }
  };

  const title = p.mode === 'shortcuts' ? 'Keyboard shortcuts' : p.mode === 'palette' ? 'Command palette' : p.searchTitle ?? 'Search the board';

  return (
    <div className="rlk-backdrop" onMouseDown={(e) => e.target === e.currentTarget && p.onClose()} data-testid={`keymap-${p.mode}`}>
      <div className={`rlk-panel${p.mode === 'shortcuts' ? ' is-wide' : ''}`} role="dialog" aria-label={title} onKeyDown={onKeyDown} tabIndex={-1}>
        <div className="rlk-head">
          {p.mode === 'shortcuts' ? (
            <h2>
              {title} <span style={{ opacity: 0.55, fontWeight: 400, fontSize: 12 }}>(same as Miro)</span>
            </h2>
          ) : (
            <input
              ref={input}
              className="rlk-input"
              placeholder={p.mode === 'palette' ? 'Type a command…' : 'Find text on the board…'}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label={title}
              data-testid={`keymap-${p.mode}-input`}
            />
          )}
          {p.mode === 'shortcuts' ? <input ref={input} aria-hidden style={{ position: 'absolute', opacity: 0, width: 1, height: 1 }} readOnly /> : null}
          <button className="rlk-x" onClick={p.onClose} aria-label="Close">
            ×
          </button>
        </div>
        <div className="rlk-body">
          {p.mode === 'shortcuts' ? <CheatSheet keymap={keymap} platform={platform} notes={p.notes} /> : null}
          {p.mode === 'shortcuts' && p.footnote ? <p className="rlk-empty" data-testid="keymap-footnote">{p.footnote}</p> : null}
          {p.mode === 'palette'
            ? commands.length
              ? commands.map((c, i) => (
                  <button key={c.id} className={`rlk-item${i === active ? ' is-active' : ''}`} onMouseEnter={() => setActive(i)} onClick={() => choose(i)}>
                    <span>{c.label}</span>
                    <span className="rlk-key">{describe(c, platform)}</span>
                  </button>
                ))
              : <div className="rlk-empty">No matching command</div>
            : null}
          {p.mode === 'search'
            ? query.trim() && !hits.length
              ? <div className="rlk-empty">No matches</div>
              : hits.map((h, i) => (
                  <button key={h.id} className={`rlk-item${i === active ? ' is-active' : ''}`} onMouseEnter={() => setActive(i)} onClick={() => choose(i)} data-testid="keymap-search-hit">
                    <span>{h.label}</span>
                    {h.detail ? <small>{h.detail}</small> : null}
                  </button>
                ))
            : null}
        </div>
      </div>
    </div>
  );
}

function CheatSheet({ keymap, platform, notes }: { keymap: readonly KeyBinding[]; platform: Platform; notes?: Partial<Record<CommandId, string>> }) {
  const groups = (Object.keys(GROUP_TITLES) as KeyGroup[]).map((g) => ({ g, rows: keymap.filter((b) => b.group === g) })).filter((x) => x.rows.length);
  return (
    <div className="rlk-cols">
      {groups.map(({ g, rows }) => (
        <section key={g} className="rlk-group">
          <h3>{GROUP_TITLES[g]}</h3>
          {rows.map((b) => (
            <div key={b.id} className="rlk-row" data-command={b.id}>
              <span>
                {b.label}
                {notes?.[b.id] ? <small>{notes[b.id]}</small> : null}
              </span>
              <span className="rlk-key">{describe(b, platform)}</span>
            </div>
          ))}
        </section>
      ))}
    </div>
  );
}
